import React, { useEffect, useMemo, useRef, useState } from 'react';
import './deck.css';
import { DeckCard } from './DeckCard';
import { DeckContext } from './DeckContext';
import { DeckRail } from './DeckRail';
import type { DeckAnswer, DeckCardModel, DeckContextState, DeckLabels, DeckOption, DeckRailState, DeckWork } from './types';

/*
 * The stack. Cards keep their DOM between steps so the next two sit behind
 * the top one, an answered card slides out to the left, a skipped one to the
 * right, and new cards deal in. The caller owns the data: it answers, skips,
 * reads documents, and may hand back new cards to deal.
 */

type Leaving = 'left' | 'right';

export interface OrbitDeckProps {
  cards: DeckCardModel[];
  /** Answer one card. Return cards to deal on top, if any. */
  onAnswer: (card: DeckCardModel, answer: DeckAnswer) => Promise<DeckCardModel[] | void> | DeckCardModel[] | void;
  onSkip: (card: DeckCardModel) => Promise<void> | void;
  /** A document dropped on a card. Return ids of cards it answered, plus cards to deal. */
  onDocument: (card: DeckCardModel, file: File) => Promise<{ removed?: string[]; insert?: DeckCardModel[] } | void> | void;
  onDone?: () => void;
  transcribe?: (blob: Blob) => Promise<string>;
  searchTools?: (query: string) => Promise<DeckOption[]>;
  rail?: DeckRailState | null;
  /** Add context beside the deck: a file, a note, a link. */
  context?: DeckContextState | null;
  labels?: DeckLabels;
  head?: React.ReactNode;
  /** Lets the caller remove cards it answered on its own, for example after a read. */
  removedIds?: string[];
  /** What Orbit is doing right now. Shown on the top card while it runs. */
  work?: DeckWork | null;
  /** The website read has shown its last pass. */
  onReadDone?: () => void;
  /** Move an answered card on at once and save it behind the deck; a save
   *  that fails brings the card back with what was typed. */
  optimistic?: boolean;
}

const SAVE_FAILED = 'That answer did not save. Try again.';

export const OrbitDeck: React.FC<OrbitDeckProps> = ({ cards: incoming, onAnswer, onSkip, onDocument, onDone, transcribe, searchTools, rail, context = null, labels = {}, head, removedIds = [], work = null, onReadDone, optimistic = false }) => {
  const [cards, setCards] = useState<DeckCardModel[]>(incoming);
  const [done, setDone] = useState<Record<string, Leaving>>({});
  const [dealt, setDealt] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState('');
  const finished = useRef(false);
  const dealtTimers = useRef<Record<string, number>>({});
  // Background saves run one after another, so answers land in the order given.
  const saving = useRef<Promise<void>>(Promise.resolve());
  const [pending, setPending] = useState(0);
  const [savedNote, setSavedNote] = useState(0);

  // A card the caller adds later lands where the caller put it, not at the
  // back. The read's confirm card is the case that matters: it exists to take
  // questions OUT of the deck, so arriving after them would be arriving too
  // late to do its job.
  useEffect(() => {
    setCards((prev) => {
      const known = new Set(prev.map((c) => c.id));
      const fresh = incoming.filter((c) => !known.has(c.id));
      if (fresh.length === 0 && prev.length === incoming.length) return prev;
      const out = prev.filter((c) => incoming.some((n) => n.id === c.id) || done[c.id]);
      fresh.forEach((card) => {
        const at = incoming.findIndex((c) => c.id === card.id);
        let anchor = -1;
        for (let i = at - 1; i >= 0; i -= 1) {
          const found = out.findIndex((c) => c.id === incoming[i].id);
          if (found >= 0) { anchor = found; break; }
        }
        out.splice(anchor + 1, 0, card);
      });
      return out;
    });
  }, [incoming, done]);

  useEffect(() => {
    if (removedIds.length === 0) return;
    setDone((prev) => {
      const next = { ...prev };
      removedIds.forEach((id) => { if (!next[id]) next[id] = 'right'; });
      return next;
    });
  }, [removedIds]);

  // One timer per card, not one for the deck: the caller reloads its data after
  // every answer, and a timer keyed on the whole hand was cancelled by each
  // reload, so no card was ever marked dealt and every card kept animating.
  useEffect(() => {
    cards.forEach((c) => {
      if (dealt[c.id] || dealtTimers.current[c.id]) return;
      dealtTimers.current[c.id] = window.setTimeout(() => {
        delete dealtTimers.current[c.id];
        setDealt((prev) => ({ ...prev, [c.id]: true }));
      }, 900);
    });
  }, [cards, dealt]);
  useEffect(() => () => {
    Object.values(dealtTimers.current).forEach((t) => window.clearTimeout(t));
  }, []);

  const live = useMemo(() => cards.filter((c) => !done[c.id]), [cards, done]);
  const total = cards.length;
  const doneCount = total - live.length;

  // The caller hands a fresh onDone on every render, so having it in the deps
  // meant each re-render cancelled the pending timer, and `finished` had already
  // been set, so it never re-armed: the deck emptied and nothing happened. The
  // callback lives in a ref; only the counts decide when to fire.
  const onDoneRef = useRef(onDone);
  useEffect(() => { onDoneRef.current = onDone; });
  useEffect(() => {
    // A deck that empties and then refills (a follow-up dealt on the answer
    // that emptied it) has not finished: the latch is lifted so the real end
    // can fire. Left set, the first empty moment swallowed every later one.
    if (live.length > 0) { finished.current = false; return undefined; }
    // A save still running may yet bring its card back.
    if (pending > 0) return undefined;
    if (finished.current || total === 0) return undefined;
    finished.current = true;
    const t = window.setTimeout(() => {
      const done = onDoneRef.current;
      if (done) done();
    }, 650);
    return () => window.clearTimeout(t);
  }, [live.length, total, pending]);

  useEffect(() => {
    if (!savedNote) return undefined;
    const t = window.setTimeout(() => setSavedNote(0), 1800);
    return () => window.clearTimeout(t);
  }, [savedNote]);

  const leave = (id: string, how: Leaving) => setDone((prev) => ({ ...prev, [id]: how }));
  const insert = (fresh: DeckCardModel[] | void) => {
    if (!fresh || fresh.length === 0) return;
    setCards((prev) => {
      const ids = new Set(prev.map((c) => c.id));
      const add = fresh.filter((c) => !ids.has(c.id));
      if (add.length === 0) return prev;
      const firstLive = prev.findIndex((c) => !done[c.id]);
      const at = firstLive < 0 ? prev.length : firstLive;
      return [...prev.slice(0, at), ...add, ...prev.slice(at)];
    });
  };

  const saveBehind = (card: DeckCardModel, value: DeckAnswer) => {
    setFailed('');
    leave(card.id, 'left');
    setSavedNote((n) => n + 1);
    setPending((n) => n + 1);
    saving.current = saving.current.then(async () => {
      try {
        const fresh = await onAnswer(card, value);
        window.setTimeout(() => insert(fresh || undefined), 420);
      } catch {
        setSavedNote(0);
        setDone((prev) => {
          const next = { ...prev };
          delete next[card.id];
          return next;
        });
        setFailed(SAVE_FAILED);
      } finally {
        setPending((n) => n - 1);
      }
    });
  };

  const answer = async (card: DeckCardModel, value: DeckAnswer) => {
    if (busy) return;
    if (optimistic) {
      saveBehind(card, value);
      return;
    }
    setBusy(true);
    setFailed('');
    try {
      const fresh = await onAnswer(card, value);
      leave(card.id, 'left');
      window.setTimeout(() => insert(fresh || undefined), 420);
    } catch (error) {
      setFailed((error as Error).message || 'That did not go through. Try again.');
    } finally {
      setBusy(false);
    }
  };
  const skip = async (card: DeckCardModel) => {
    if (busy) return;
    if (card.noSkip && !card.body) return;
    setBusy(true);
    try {
      await onSkip(card);
      leave(card.id, 'right');
    } finally {
      setBusy(false);
    }
  };
  const document = async (card: DeckCardModel, file: File) => {
    if (busy) return;
    setBusy(true);
    try {
      const result = await onDocument(card, file);
      if (result && result.removed) {
        window.setTimeout(() => {
          result.removed!.forEach((id) => leave(id, 'right'));
        }, 700);
      }
      if (card.input === 'evidence') leave(card.id, 'left');
      if (result && result.insert) window.setTimeout(() => insert(result.insert), 900);
    } finally {
      setBusy(false);
    }
  };

  const top = live[0];
  const skipWord = labels.skipWord || 'skip';

  const frame = (
    <div>
      {head}
      <div className="odeck-frame">
        <div className="odeck-frame-head">
          <span className="fk">{labels.deckTitle || 'The deck'}<small>{labels.deckNote || (live.length > 0 ? `${live.length} in the deck` : 'deck complete')}</small></span>
          {savedNote > 0 && <span className="odeck-saved" role="status">Saved ✓</span>}
          <span className="odeck-count">CARD <b>{String(Math.min(doneCount + 1, Math.max(total, 1))).padStart(2, '0')}</b> · {String(Math.max(total, 1)).padStart(2, '0')}</span>
        </div>
        <div className="odeck">
          {cards.map((card) => {
            const leaving = done[card.id];
            const idx = live.indexOf(card);
            let pos = 'hidden';
            if (idx === 0) pos = '0';
            if (idx === 1) pos = '1';
            if (idx === 2) pos = '2';
            let cls = 'odeck-card';
            // Never on a card that is leaving: the deal animation sets opacity
            // and an animation beats the `opacity: 0` that .gone-* declares, so
            // the card would slide out and stay visible.
            if (!dealt[card.id] && !leaving) cls += ' deal';
            if (leaving === 'left') cls += ' gone-left';
            if (leaving === 'right') cls += ' gone-right';
            let number = doneCount + idx;
            if (leaving) number = cards.indexOf(card);
            let cardError = '';
            let cardWork: DeckWork | null = null;
            if (idx === 0) {
              cardError = failed;
              // A panel that names its card waits for that card. The read starts
              // before the card it belongs to is reached, and the card in front
              // of it must not be taken over by somebody else's work.
              if (!work || !work.cardId || work.cardId === card.id) cardWork = work;
            }
            return (
              <article key={card.id} className={cls} data-pos={leaving ? undefined : pos} style={{ zIndex: leaving ? 40 : 30 - Math.max(idx, 0), animationDelay: `${Math.min(Math.max(idx, 0), 3) * 110}ms` }} aria-hidden={idx !== 0}>
                <DeckCard
                  card={card}
                  index={number}
                  total={total}
                  busy={busy}
                  labels={labels}
                  transcribe={transcribe}
                  searchTools={searchTools}
                  error={cardError}
                  work={cardWork}
                  onReadDone={onReadDone}
                  onAnswer={(a) => answer(card, a)}
                  onSkip={() => skip(card)}
                  onDocument={(file) => document(card, file)}
                />
              </article>
            );
          })}
        </div>
        {(!top || !top.noSkip) && (
        <div className="odeck-frame-foot">
          <span>Answer another way, always:</span>
          <span className="ways">
            {transcribe && <><button type="button" onClick={() => { /* the card's own mic handles recording */ }}>voice</button><i>·</i></>}
            <button type="button" onClick={() => { const input = window.document.querySelector<HTMLInputElement>('.odeck-card[data-pos="0"] input[type=file]'); if (input) input.click(); }}>doc</button>
            <i>·</i>
            <button type="button" onClick={() => { if (top) skip(top); }}>{skipWord}</button>
          </span>
        </div>
        )}
      </div>
    </div>
  );

  let wrap = 'odeck-wrap solo';
  if (rail || context) wrap = 'odeck-wrap';

  return (
    <div className={wrap}>
      {frame}
      {(rail || context) && (
        <div className="odeck-side">
          {context && <DeckContext ctx={context} />}
          {rail && <DeckRail rail={rail} />}
        </div>
      )}
    </div>
  );
};

export default OrbitDeck;
